/**
 * Warehouse data access for the admin SPA (#304).
 *
 * Mirrors go-service/internal/api/warehouses.go: GET/POST/PATCH/DELETE ?id= on
 * /api/admin/warehouses, plus PUT /api/admin/equipment/{id}/warehouse-stock.
 *
 * A Warehouse is one Company site: it holds per-equipment stock AND is a
 * transport origin. Every Company has at least one (migration 128 seeds it),
 * so the load-bearing contract for callers is the count, not emptiness: at
 * exactly ONE active warehouse the app renders as it did before #304, and the
 * per-warehouse UI appears only above that.
 */
import { apiFetch } from "../../../shared/api-client";
import { resource } from "./resource";

export interface CompanyWarehouse {
  id: string;
  name: string;
  address: string;
  /** Geocoded from the address server-side; null when it could not resolve. */
  lat: number | null;
  lng: number | null;
  active: boolean;
  sort_order: number;
  created_at?: string | null;
}

/** Create payload — name required, address optional (defaults to blank). */
export interface WarehouseCreate {
  name: string;
  address: string;
}

/** Update payload — id required; only supplied keys are written server-side. */
export interface WarehouseUpdate extends Partial<WarehouseCreate> {
  id: string;
  active?: boolean;
}

/** One Warehouse's holding of one Equipment row. */
export interface WarehouseStockEntry {
  warehouse_id: string;
  warehouse_name: string;
  quantity: number;
}

/**
 * A write's result: the saved row plus whether its address resolved to
 * coordinates. `geocoded: false` with a non-empty address is what the settings
 * banner reports — the save succeeded, but transport cannot measure from here.
 */
export interface WarehouseSaved {
  warehouse: CompanyWarehouse;
  geocoded: boolean;
}

const warehouses = resource<CompanyWarehouse[]>(
  "/api/admin/warehouses",
  "warehouses",
);
const warehouse = resource<CompanyWarehouse>("/api/admin/warehouses");

/**
 * Fetch every warehouse, active and retired (the server does not filter — the
 * caller shows active ones and keeps retired names resolvable for history).
 */
export const listWarehouses = async (
  signal?: AbortSignal,
): Promise<CompanyWarehouse[]> => (await warehouses.get(signal)) ?? [];

const saved = (res: {
  warehouse?: CompanyWarehouse;
  geocoded?: unknown;
}): WarehouseSaved => {
  if (!res?.warehouse) throw new Error("Expected a warehouse");
  return { warehouse: res.warehouse, geocoded: res.geocoded === true };
};

export const createWarehouse = async (
  payload: WarehouseCreate,
): Promise<WarehouseSaved> =>
  saved(
    await apiFetch<{ warehouse?: CompanyWarehouse; geocoded?: unknown }>(
      "/api/admin/warehouses",
      { method: "POST", body: payload },
    ),
  );

export const updateWarehouse = async (
  payload: WarehouseUpdate,
): Promise<WarehouseSaved> =>
  saved(
    await apiFetch<{ warehouse?: CompanyWarehouse; geocoded?: unknown }>(
      "/api/admin/warehouses",
      { method: "PATCH", body: payload },
    ),
  );

/**
 * Retire a warehouse — a soft delete, like a vehicle: stock rows and booking
 * lines keep pointing at it, so it is never removable. The server refuses
 * (400) on the last active one; stock has to live somewhere.
 */
export const retireWarehouse = (id: string): Promise<void> =>
  warehouse.del({ id });

/**
 * Set (or clear) one Equipment row's stock at one Warehouse. `quantity: null`
 * clears the row entirely — "does not hold it" and "holds none of it" are one
 * fact. Returns the whole split so the caller redraws from the server's answer
 * rather than its own arithmetic.
 */
export const putWarehouseStock = async (
  equipmentId: string,
  warehouseId: string,
  quantity: number | null,
): Promise<WarehouseStockEntry[]> => {
  const res = await apiFetch<{ warehouse_stock?: WarehouseStockEntry[] }>(
    `/api/admin/equipment/${equipmentId}/warehouse-stock`,
    { method: "PUT", body: { warehouse_id: warehouseId, quantity } },
  );
  return res.warehouse_stock ?? [];
};
